import React, { useEffect, useRef, useState } from 'react';
import { admin, files } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Upload, Save, PlayCircle, Trash2, Eye, EyeOff, Youtube, Film } from 'lucide-react';

/**
 * Admin tile — manage the intro video that renders at the top of the Blackout
 * Game page for all users. Supports two modes:
 *   - "upload": upload a MP4/MOV/WEBM via /api/upload?kind=video
 *   - "embed":  paste a YouTube or Vimeo share URL
 */
export default function AdminBlackoutIntroVideoEditor() {
  const [state, setState] = useState({ loading: true, data: null });
  const [draft, setDraft] = useState({ enabled: false, type: 'embed', url: '', title: '', description: '' });
  const [busy, setBusy] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    setState({ loading: true, data: null });
    try {
      const d = await admin.getBlackoutIntroVideo();
      setState({ loading: false, data: d });
      setDraft({
        enabled: !!d?.enabled,
        type: d?.type || 'embed',
        url: d?.url || '',
        title: d?.title || '',
        description: d?.description || '',
      });
    } catch (err) {
      setState({ loading: false, data: null });
      toast({ title: 'Load failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!draft.url?.trim()) {
      toast({ title: 'URL is required', description: 'Upload a video or paste a YouTube / Vimeo link first.' });
      return;
    }
    setBusy('save');
    try {
      await admin.updateBlackoutIntroVideo({
        enabled: draft.enabled,
        type: draft.type,
        url: draft.url.trim(),
        title: draft.title.trim(),
        description: draft.description.trim(),
      });
      toast({ title: draft.enabled ? 'Video saved — now live' : 'Draft saved' });
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(null);
    }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) {
      toast({ title: 'Only video files allowed' });
      return;
    }
    setBusy('upload'); setUploadPct(0);
    try {
      const r = await files.upload(f, 'video');
      if (!r?.url) throw new Error('Upload returned no URL');
      setDraft(d => ({ ...d, type: 'upload', url: r.url }));
      setUploadPct(100);
      toast({ title: 'Video uploaded', description: 'Click Save to publish.' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const clearVideo = async () => {
    if (!window.confirm('Remove the current video? The Blackout Game page will stop showing it.')) return;
    setBusy('clear');
    try {
      await admin.updateBlackoutIntroVideo({ enabled: false, url: '' });
      toast({ title: 'Video removed' });
      load();
    } catch (err) {
      toast({ title: 'Remove failed', description: err?.response?.data?.detail });
    } finally { setBusy(null); }
  };

  if (state.loading) {
    return (
      <section className="rounded-xl border border-neutral-800 bg-neutral-950 p-4">
        <div className="text-neutral-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading video settings…</div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/20 via-neutral-950 to-neutral-950 p-4 md:p-5 space-y-4"
             data-testid="admin-blackout-intro-video">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-black text-base flex items-center gap-2 text-white">
            <PlayCircle size={16} className="text-emerald-400"/> Blackout Intro Video
          </h3>
          <p className="text-[11px] text-neutral-400 mt-0.5">
            Shown at the top of /blackout and /blackout-info for all users. Upload a file or paste a YouTube / Vimeo link.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${state.data?.enabled ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-neutral-500/10 border-neutral-500/30 text-neutral-400'}`}>
            {state.data?.enabled ? 'Live' : 'Hidden'}
          </span>
        </div>
      </header>

      {/* Source mode toggle */}
      <div className="inline-flex rounded-full border border-neutral-800 overflow-hidden text-xs">
        <button onClick={() => setDraft(d => ({ ...d, type: 'embed' }))}
          data-testid="intro-video-mode-embed"
          className={`px-3 py-1.5 inline-flex items-center gap-1 ${draft.type === 'embed' ? 'bg-emerald-600 text-black font-semibold' : 'text-neutral-400'}`}>
          <Youtube size={12}/> YouTube / Vimeo embed
        </button>
        <button onClick={() => setDraft(d => ({ ...d, type: 'upload' }))}
          data-testid="intro-video-mode-upload"
          className={`px-3 py-1.5 inline-flex items-center gap-1 ${draft.type === 'upload' ? 'bg-emerald-600 text-black font-semibold' : 'text-neutral-400'}`}>
          <Film size={12}/> Upload file
        </button>
      </div>

      {draft.type === 'embed' ? (
        <div>
          <label className="text-xs font-semibold text-neutral-300 block mb-1">YouTube or Vimeo URL</label>
          <input type="url" value={draft.url} onChange={e => setDraft(d => ({ ...d, url: e.target.value }))}
            placeholder="https://www.youtube.com/watch?v=…  or  https://vimeo.com/…"
            data-testid="intro-video-embed-url"
            className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-sm text-white"/>
          <p className="text-[10px] text-neutral-500 mt-1">We'll convert the link to an embed automatically.</p>
          {draft.url && /youtube\.com\/shorts\//i.test(draft.url) && (
            <p className="text-[11px] text-amber-300 mt-2 flex items-start gap-1.5"
               data-testid="intro-video-shorts-warning">
              <span>⚠️</span>
              <span>
                <b>YouTube Shorts tip:</b> Shorts often have "Allow embedding" turned OFF by default and will show "Video unavailable" on the live site. In YouTube Studio open this video → <i>Edit → Advanced settings</i> → tick <b>Allow embedding</b>, or paste the regular <code className="bg-black/40 px-1 rounded">watch?v=</code> URL instead.
              </span>
            </p>
          )}
        </div>
      ) : (
        <div>
          <label className="text-xs font-semibold text-neutral-300 block mb-1">Video file (MP4, MOV, WEBM)</label>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" onChange={onUpload} className="hidden"
              data-testid="intro-video-file-input"/>
            <button onClick={() => fileRef.current?.click()} disabled={busy === 'upload'}
              data-testid="intro-video-upload-button"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-black font-semibold text-xs disabled:opacity-60">
              {busy === 'upload' ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
              {busy === 'upload' ? `Uploading… ${uploadPct}%` : 'Choose video'}
            </button>
            {draft.url && draft.type === 'upload' && (
              <a href={draft.url} target="_blank" rel="noreferrer" className="text-xs text-emerald-300 underline break-all">
                Current file ↗
              </a>
            )}
          </div>
        </div>
      )}

      {/* Title / description */}
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-neutral-300 block mb-1">Title (optional)</label>
          <input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
            maxLength={160} placeholder="How The Blackout Game works"
            data-testid="intro-video-title"
            className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-sm text-white"/>
        </div>
        <div>
          <label className="text-xs font-semibold text-neutral-300 block mb-1">Short description (optional)</label>
          <input value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
            maxLength={240} placeholder="Watch this 90-second walkthrough before you start playing."
            data-testid="intro-video-description"
            className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-sm text-white"/>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <label className="flex items-center gap-2 text-xs text-neutral-300">
          <input type="checkbox" checked={!!draft.enabled} onChange={e => setDraft(d => ({ ...d, enabled: e.target.checked }))}
            data-testid="intro-video-enabled"
            className="accent-emerald-500 w-4 h-4"/>
          {draft.enabled ? <><Eye size={13}/> Visible on Blackout Game page</> : <><EyeOff size={13}/> Hidden from users</>}
        </label>
        <div className="flex items-center gap-2">
          {state.data?.url && (
            <button onClick={clearVideo} disabled={busy === 'clear'}
              data-testid="intro-video-clear"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10">
              <Trash2 size={11}/> Remove
            </button>
          )}
          <button onClick={save} disabled={busy === 'save'}
            data-testid="intro-video-save"
            className="inline-flex items-center gap-1 text-xs px-4 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold disabled:opacity-60">
            {busy === 'save' ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save
          </button>
        </div>
      </div>
    </section>
  );
}
